/*!
 * @pixi/filter-tilt-shift - v3.0.3
 * Compiled Wed, 29 May 2019 03:04:05 UTC
 *
 * @pixi/filter-tilt-shift is licensed under the MIT License.
 * http://www.opensource.org/licenses/mit-license
 */
import { Filter } from '@pixi/core';
import { Point } from '@pixi/math';
var vertex = "attribute vec2 aVertexPosition;\nattribute vec2 aTextureCoord;\n\nuniform mat3 projectionMatrix;\n\nvarying vec2 vTextureCoord;\n\nvoid main(void)\n{\n    gl_Position = vec4((projectionMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);\n    vTextureCoord = aTextureCoord;\n}";
var fragment = "varying vec2 vTextureCoord;\n\nuniform sampler2D uSampler;\nuniform float blur;\nuniform float gradientBlur;\nuniform vec2 start;\nuniform vec2 end;\nuniform vec2 delta;\nuniform vec2 texSize;\n\nfloat random(vec3 scale, float seed)\n{\n    return fract(sin(dot(gl_FragCoord.xyz + seed, scale)) * 43758.5453 + seed);\n}\n\nvoid main(void)\n{\n    vec4 color = vec4(0.0);\n    float total = 0.0;\n\n    float offset = random(vec3(12.9898, 78.233, 151.7182), 0.0);\n    vec2 normal = normalize(vec2(start.y - end.y, end.x - start.x));\n    float radius = smoothstep(0.0, 1.0, abs(dot(vTextureCoord * texSize - start, normal)) / gradientBlur) * blur;\n\n    for (float t = -30.0; t <= 30.0; t++)\n    {\n        float percent = (t + offset - 0.5) / 30.0;\n        float weight = 1.0 - abs(percent);\n        vec4 sample = texture2D(uSampler, vTextureCoord + delta / texSize * percent * radius);\n        sample.rgb *= sample.a;\n        color += sample * weight;\n        total += weight;\n    }\n\n    color /= total;\n    color.rgb /= color.a + 0.00001;\n\n    gl_FragColor = color;\n}\n";
/**
 * @author Vico @vicocotea
 * original filter https://github.com/evanw/glfx.js/blob/master/src/filters/blur/tiltshift.js by Evan Wallace : http://madebyevan.com/
 */

/**
 * A TiltShiftAxisFilter.
 *
 * @class
 * @extends PIXI.Filter
 * @memberof PIXI.filters
 * @private
 */

var TiltShiftAxisFilter =
/*@__PURE__*/
function (Filter) {
  function TiltShiftAxisFilter(blur, gradientBlur, start, end) {
    if (blur === void 0) blur = 100;
    if (gradientBlur === void 0) gradientBlur = 600;
    if (start === void 0) start = null;
    if (end === void 0) end = null;
    Filter.call(this, vertex, fragment);
    this.uniforms.blur = blur;
    this.uniforms.gradientBlur = gradientBlur;
    this.uniforms.start = start || new Point(0, window.innerHeight / 2);
    this.uniforms.end = end || new Point(600, window.innerHeight / 2);
    this.uniforms.delta = new Point(30, 30);
    this.uniforms.texSize = new Point(window.innerWidth, window.innerHeight);
    this.updateDelta();
  }

  if (Filter) TiltShiftAxisFilter.__proto__ = Filter;
  TiltShiftAxisFilter.prototype = Object.create(Filter && Filter.prototype);
  TiltShiftAxisFilter.prototype.constructor = TiltShiftAxisFilter;
  var prototypeAccessors = {
    blur: {
      configurable: true
    },
    gradientBlur: {
      configurable: true
    },
    start: {
      configurable: true
    },
    end: {
      configurable: true
    }
  };
  /**
   * Updates the filter delta values.
   * This is overridden in the X and Y filters, does nothing for this class.
   *
   */

  TiltShiftAxisFilter.prototype.updateDelta = function updateDelta() {
    this.uniforms.delta.x = 0;
    this.uniforms.delta.y = 0;
  };
  /**
   * The strength of the blur.
   *
   * @member {number}
   * @memberof PIXI.filters.TiltShiftAxisFilter#
   */


  prototypeAccessors.blur.get = function () {
    return this.uniforms.blur;
  };

  prototypeAccessors.blur.set = function (value) {
    this.uniforms.blur = value;
  };
  /**
   * The strength of the gradient blur.
   *
   * @member {number}
   * @memberof PIXI.filters.TiltShiftAxisFilter#
   */


  prototypeAccessors.gradientBlur.get = function () {
    return this.uniforms.gradientBlur;
  };

  prototypeAccessors.gradientBlur.set = function (value) {
    this.uniforms.gradientBlur = value;
  };
  /**
   * The X value to start the effect at.
   *
   * @member {PIXI.Point}
   * @memberof PIXI.filters.TiltShiftAxisFilter#
   */


  prototypeAccessors.start.get = function () {
    return this.uniforms.start;
  };

  prototypeAccessors.start.set = function (value) {
    this.uniforms.start = value;
    this.updateDelta();
  };
  /**
   * The X value to end the effect at.
   *
   * @member {PIXI.Point}
   * @memberof PIXI.filters.TiltShiftAxisFilter#
   */


  prototypeAccessors.end.get = function () {
    return this.uniforms.end;
  };

  prototypeAccessors.end.set = function (value) {
    this.uniforms.end = value;
    this.updateDelta();
  };

  Object.defineProperties(TiltShiftAxisFilter.prototype, prototypeAccessors);
  return TiltShiftAxisFilter;
}(Filter);
/**
 * @author Vico @vicocotea
 * original filter https://github.com/evanw/glfx.js/blob/master/src/filters/blur/tiltshift.js by Evan Wallace : http://madebyevan.com/
 */

/**
 * A TiltShiftXFilter.
 *
 * @class
 * @extends PIXI.TiltShiftAxisFilter
 * @memberof PIXI.filters
 * @private
 */


var TiltShiftXFilter =
/*@__PURE__*/
function (TiltShiftAxisFilter) {
  function TiltShiftXFilter() {
    TiltShiftAxisFilter.apply(this, arguments);
  }

  if (TiltShiftAxisFilter) TiltShiftXFilter.__proto__ = TiltShiftAxisFilter;
  TiltShiftXFilter.prototype = Object.create(TiltShiftAxisFilter && TiltShiftAxisFilter.prototype);
  TiltShiftXFilter.prototype.constructor = TiltShiftXFilter;

  TiltShiftXFilter.prototype.updateDelta = function updateDelta() {
    var dx = this.uniforms.end.x - this.uniforms.start.x;
    var dy = this.uniforms.end.y - this.uniforms.start.y;
    var d = Math.sqrt(dx * dx + dy * dy);
    this.uniforms.delta.x = dx / d;
    this.uniforms.delta.y = dy / d;
  };

  return TiltShiftXFilter;
}(TiltShiftAxisFilter);
/**
 * @author Vico @vicocotea
 * original filter https://github.com/evanw/glfx.js/blob/master/src/filters/blur/tiltshift.js by Evan Wallace : http://madebyevan.com/
 */

/**
 * A TiltShiftYFilter.
 *
 * @class
 * @extends PIXI.TiltShiftAxisFilter
 * @memberof PIXI.filters
 * @private
 */


var TiltShiftYFilter =
/*@__PURE__*/
function (TiltShiftAxisFilter) {
  function TiltShiftYFilter() {
    TiltShiftAxisFilter.apply(this, arguments);
  }

  if (TiltShiftAxisFilter) TiltShiftYFilter.__proto__ = TiltShiftAxisFilter;
  TiltShiftYFilter.prototype = Object.create(TiltShiftAxisFilter && TiltShiftAxisFilter.prototype);
  TiltShiftYFilter.prototype.constructor = TiltShiftYFilter;

  TiltShiftYFilter.prototype.updateDelta = function updateDelta() {
    var dx = this.uniforms.end.x - this.uniforms.start.x;
    var dy = this.uniforms.end.y - this.uniforms.start.y;
    var d = Math.sqrt(dx * dx + dy * dy);
    this.uniforms.delta.x = -dy / d;
    this.uniforms.delta.y = dx / d;
  };

  return TiltShiftYFilter;
}(TiltShiftAxisFilter);
/**
 * @author Vico @vicocotea
 * original filter https://github.com/evanw/glfx.js/blob/master/src/filters/blur/tiltshift.js by Evan Wallace : http://madebyevan.com/
 */

/**
 * A TiltShift Filter. Manages the pass of both a TiltShiftXFilter and TiltShiftYFilter.<br>
 * ![original](../tools/screenshots/dist/original.png)![filter](../tools/screenshots/dist/tilt-shift.png)
 *
 * @class
 * @extends PIXI.Filter
 * @memberof PIXI.filters
 * @see {@link https://www.npmjs.com/package/@pixi/filter-tilt-shift|@pixi/filter-tilt-shift}
 * @see {@link https://www.npmjs.com/package/pixi-filters|pixi-filters}
 * @param {number} [blur=100] The strength of the blur.
 * @param {number} [gradientBlur=600] The strength of the gradient blur.
 * @param {PIXI.Point} [start=null] The Y value to start the effect at.
 * @param {PIXI.Point} [end=null] The Y value to end the effect at.
 */


var TiltShiftFilter =
/*@__PURE__*/
function (Filter) {
  function TiltShiftFilter(blur, gradientBlur, start, end) {
    if (blur === void 0) blur = 100;
    if (gradientBlur === void 0) gradientBlur = 600;
    if (start === void 0) start = null;
    if (end === void 0) end = null;
    Filter.call(this);
    this.tiltShiftXFilter = new TiltShiftXFilter(blur, gradientBlur, start, end);
    this.tiltShiftYFilter = new TiltShiftYFilter(blur, gradientBlur, start, end);
  }

  if (Filter) TiltShiftFilter.__proto__ = Filter;
  TiltShiftFilter.prototype = Object.create(Filter && Filter.prototype);
  TiltShiftFilter.prototype.constructor = TiltShiftFilter;
  var prototypeAccessors = {
    blur: {
      configurable: true
    },
    gradientBlur: {
      configurable: true
    },
    start: {
      configurable: true
    },
    end: {
      configurable: true
    }
  };

  TiltShiftFilter.prototype.apply = function apply(filterManager, input, output) {
    var renderTarget = filterManager.getFilterTexture();
    this.tiltShiftXFilter.apply(filterManager, input, renderTarget);
    this.tiltShiftYFilter.apply(filterManager, renderTarget, output);
    filterManager.returnFilterTexture(renderTarget);
  };
  /**
   * The strength of the blur.
   *
   * @member {number}
   */


  prototypeAccessors.blur.get = function () {
    return this.tiltShiftXFilter.blur;
  };

  prototypeAccessors.blur.set = function (value) {
    this.tiltShiftXFilter.blur = this.tiltShiftYFilter.blur = value;
  };
  /**
   * The strength of the gradient blur.
   *
   * @member {number}
   */


  prototypeAccessors.gradientBlur.get = function () {
    return this.tiltShiftXFilter.gradientBlur;
  };

  prototypeAccessors.gradientBlur.set = function (value) {
    this.tiltShiftXFilter.gradientBlur = this.tiltShiftYFilter.gradientBlur = value;
  };
  /**
   * The Y value to start the effect at.
   *
   * @member {PIXI.Point}
   */


  prototypeAccessors.start.get = function () {
    return this.tiltShiftXFilter.start;
  };

  prototypeAccessors.start.set = function (value) {
    this.tiltShiftXFilter.start = this.tiltShiftYFilter.start = value;
  };
  /**
   * The Y value to end the effect at.
   *
   * @member {PIXI.Point}
   */


  prototypeAccessors.end.get = function () {
    return this.tiltShiftXFilter.end;
  };

  prototypeAccessors.end.set = function (value) {
    this.tiltShiftXFilter.end = this.tiltShiftYFilter.end = value;
  };

  Object.defineProperties(TiltShiftFilter.prototype, prototypeAccessors);
  return TiltShiftFilter;
}(Filter);

export { TiltShiftAxisFilter, TiltShiftFilter, TiltShiftXFilter, TiltShiftYFilter };